// TaskDetailPage — детальная страница задачи с actions (change status / assign / delete).
// R31 / U-3: payload смены статуса по контракту backend (newStatus), опции селекта
// локализованы (tasks.status.*) и ограничены state machine (allowedNextStatuses);
// 400/409 показываются в Alert через extractApiErrorMessage.
// R34 / U-1: кнопка «Назад» (navigate(-1)).
// R38b / U-9: ссылка на связанную сделку (dealId из V30) — название, не сырой UUID (U-15).
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  TextField,
  Typography,
  Alert,
  Link,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink } from 'react-router-dom';
import { PageHeader } from '@/theme/PageHeader';
import { LoadingSpinner } from '@/theme/LoadingSpinner';
import { EmptyState } from '@/theme/EmptyState';
import { ConfirmDialog } from '@/theme/ConfirmDialog';
import { TaskCard } from './components/TaskCard';
import { tasksApi, allowedNextStatuses, type Task, type TaskStatus, type TaskPriority } from '@/api/tasks';
import { useDealTitleOrUnavailable } from '@/hooks/useDealTitleOrUnavailable';
import { UserAutocomplete } from '@/components/UserAutocomplete';
import { extractApiErrorMessage } from '@/lib/apiError';
import { formatTaskPriority } from '@/lib/format';
import { t } from '@/i18n';

/** Все значения приоритета (зеркало backend TaskPriority enum; для select диалога). */
const PRIORITY_OPTIONS: readonly TaskPriority[] = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

/** Страница детальной информации о задаче с actions. */
export function TaskDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [priorityDialogOpen, setPriorityDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<TaskStatus>('IN_PROGRESS');
  const [newAssignee, setNewAssignee] = useState('');
  const [newPriority, setNewPriority] = useState<TaskPriority>('MEDIUM');

  const query = useQuery({
    queryKey: ['task', id],
    queryFn: () => tasksApi.get(id ?? ''),
    enabled: Boolean(id),
  });

  // R38b / U-9: название связанной сделки для ссылки (dealId nullable, V30).
  // FIX-C (S4): 404 canceled-сделки (non-admin) → «Сделка удалена» вместо ссылки на 404.
  const dealId = query.data?.dealId ?? null;
  const dealResolutions = useDealTitleOrUnavailable(dealId ? [dealId] : []);
  const dealResolution = dealId ? dealResolutions.get(dealId) : undefined;

  const statusMutation = useMutation({
    mutationFn: () => {
      // B2: optimistic-lock требует фактический updatedAt из данных — без fallback
      // «now» (он давал бесконечные 409). Данные гарантированы guard'ом рендера.
      if (!query.data) throw new Error('Данные задачи не загружены');
      return tasksApi.changeStatus(id ?? '', {
        // U-3: контракт backend TaskChangeStatusRequest — поле newStatus (не status).
        newStatus,
        expectedUpdatedAt: query.data.updatedAt,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['task', id] });
      setStatusDialogOpen(false);
    },
    onError: () => {
      // B2: 409/ошибка → refetch — свежий updatedAt, пользователь не в тупике повторных 409.
      void queryClient.invalidateQueries({ queryKey: ['task', id] });
    },
  });

  const assignMutation = useMutation({
    mutationFn: () => tasksApi.assign(id ?? '', { assigneeUserId: newAssignee }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['task', id] });
      setAssignDialogOpen(false);
    },
  });

  // V21 W2: смена приоритета — PUT c optimistic-lock; payload только priority
  // (+ фактический updatedAt), прочие поля не затираются.
  const priorityMutation = useMutation({
    mutationFn: () => {
      // B2: optimistic-lock требует фактический updatedAt из данных — без fallback «now».
      if (!query.data) throw new Error('Данные задачи не загружены');
      return tasksApi.update(id ?? '', {
        priority: newPriority,
        expectedUpdatedAt: query.data.updatedAt,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['task', id] });
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
      setPriorityDialogOpen(false);
    },
    onError: () => {
      // B2: 409/ошибка → refetch — свежий updatedAt, пользователь не в тупике повторных 409.
      void queryClient.invalidateQueries({ queryKey: ['task', id] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => tasksApi.delete(id ?? ''),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
      navigate('/tasks');
    },
  });

  if (query.isLoading) return <LoadingSpinner label="Загрузка задачи…" />;
  if (query.isError || !query.data) {
    return (
      <EmptyState
        title="Задача не найдена"
        description={query.error instanceof Error ? query.error.message : 'Неизвестная ошибка'}
        actionLabel="К списку задач"
        onAction={() => navigate('/tasks')}
      />
    );
  }
  const task: Task = query.data;
  // U-3: UI предлагает только разрешённые state machine переходы (иначе backend → 409).
  const statusTransitions = allowedNextStatuses(task.status);

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 1 }}
        aria-label={t('common.back')}
      >
        {t('common.back')}
      </Button>
      <PageHeader
        title={task.title}
        subtitle={`Задача №${task.taskNumber} · Версия: ${task.version}`}
        actions={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => {
                setNewStatus(task.status);
                setStatusDialogOpen(true);
              }}
            >
              Изменить статус
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setNewAssignee(task.assigneeUserId ?? '');
                setAssignDialogOpen(true);
              }}
            >
              Назначить
            </Button>
            {/* V21 W2: инлайн-диалог смены приоритета (канон соседних actions). */}
            <Button
              variant="outlined"
              onClick={() => {
                setNewPriority(task.priority);
                setPriorityDialogOpen(true);
              }}
            >
              {t('tasks.changePriority')}
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
            >
              Удалить
            </Button>
          </Stack>
        }
      />
      {/* R38b / U-9: ссылка на связанную сделку (название, не UUID — U-15).
          FIX-C (S4): сделка недоступна (404/cancelled) → некликабельное «Сделка удалена». */}
      {task.dealId ? (
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t('tasks.linkedDeal')}:{' '}
          {dealResolution?.unavailable ? (
            <Typography component="span" variant="body2" color="text.secondary">
              {t('tasks.dealDeleted')}
            </Typography>
          ) : dealResolution?.title ? (
            <Link component={RouterLink} to={`/crm/deals/${task.dealId}`} underline="hover">
              {dealResolution.title}
            </Link>
          ) : (
            <Typography component="span" variant="body2" color="text.secondary">
              …
            </Typography>
          )}
        </Typography>
      ) : null}
      {statusMutation.isError || assignMutation.isError || deleteMutation.isError || priorityMutation.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {extractApiErrorMessage(
            statusMutation.error ??
              assignMutation.error ??
              deleteMutation.error ??
              priorityMutation.error,
            t('tasks.operationError'),
          )}
        </Alert>
      ) : null}
      <TaskCard task={task} />

      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)}>
        <DialogTitle>Изменить статус</DialogTitle>
        <DialogContent>
          {statusTransitions.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('tasks.status.noTransitions')}
            </Typography>
          ) : (
            <TextField
              select
              fullWidth
              label="Новый статус"
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value as TaskStatus)}
              sx={{ mt: 1, minWidth: 280 }}
            >
              {statusTransitions.map((s) => (
                <MenuItem key={s} value={s}>
                  {t(`tasks.status.${s}`)}
                </MenuItem>
              ))}
            </TextField>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Отмена</Button>
          <Button
            variant="contained"
            onClick={() => statusMutation.mutate()}
            disabled={statusTransitions.length === 0 || statusMutation.isPending}
          >
            Применить
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)}>
        <DialogTitle>Назначить исполнителя</DialogTitle>
        <DialogContent>
          {/* R36b / U-2: живой поиск исполнителя вместо ручного ввода UUID. */}
          <Box sx={{ mt: 1, minWidth: 320 }}>
            <UserAutocomplete
              label="Исполнитель"
              value={newAssignee}
              onChange={setNewAssignee}
              aria-label="Исполнитель"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Отмена</Button>
          <Button
            variant="contained"
            onClick={() => assignMutation.mutate()}
            disabled={!newAssignee.trim() || assignMutation.isPending}
          >
            Назначить
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={priorityDialogOpen} onClose={() => setPriorityDialogOpen(false)}>
        <DialogTitle>{t('tasks.changePriority')}</DialogTitle>
        <DialogContent>
          {/* V21 W2: подписи через formatTaskPriority — raw enum в UI не показывается. */}
          <TextField
            select
            fullWidth
            label={t('tasks.priorityLabel')}
            value={newPriority}
            onChange={(e) => setNewPriority(e.target.value as TaskPriority)}
            sx={{ mt: 1, minWidth: 280 }}
          >
            {PRIORITY_OPTIONS.map((p) => (
              <MenuItem key={p} value={p}>
                {formatTaskPriority(p, t)}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPriorityDialogOpen(false)}>Отмена</Button>
          <Button
            variant="contained"
            onClick={() => priorityMutation.mutate()}
            disabled={priorityMutation.isPending}
          >
            Применить
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Удалить задачу?"
        message="Задача будет помечена как удалённая. Действие необратимо."
        confirmLabel="Удалить"
        confirmColor="error"
        onConfirm={() => deleteMutation.mutate()}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </Box>
  );
}
